'use client';

import { cn } from 'common/utils';
import {
  colorByValue,
  formatCurrency,
  formatPercent,
} from 'common/utils/trading-demo';

interface PortfolioSummaryCardProps {
  className?: string;
  totalValue: string;
  change: string;
  changePercent: string;
  balance?: string;
  isPortfolioInfoDataFetched?: boolean;
}

const PortfolioSummaryCard = ({
  className,
  totalValue,
  change,
  changePercent,
  balance,
  isPortfolioInfoDataFetched = false,
}: PortfolioSummaryCardProps) => {
  if (!isPortfolioInfoDataFetched) {
    return <PortfolioSummaryCardSkeleton className={className} />;
  }

  return (
    <div
      className={cn(
        'relative mb-5 space-y-4 rounded-2xl bg-white p-6 text-center shadow-sm',
        className,
      )}
    >
      <div>
        <div className="mb-1 text-xs tracking-wide text-pp-text-secondary">
          デモ総資産価値
        </div>
        <div className="text-[32px] font-bold leading-none text-pp-text-primary">
          {formatCurrency({
            value: totalValue,
            supportZero: true,
            withSign: false,
            currency: 'JPY',
          })}
        </div>
      </div>
      <div
        className={cn(
          'text-xl font-semibold',
          colorByValue(parseFloat(change), 'text-pp-text-primary'),
        )}
      >
        <div className="mb-1 text-xs font-normal text-pp-text-secondary">
          デモ評価損益
        </div>
        {formatCurrency({
          value: change,
          supportZero: true,
          withSign: true,
          currency: 'JPY',
        })}{' '}
        (
        {formatPercent({
          value: changePercent,
          supportZero: true,
          withSign: true,
        })}
        )
      </div>
      {balance && (
        <div className="text-center">
          <div className="mb-1 text-xs tracking-wide text-pp-text-secondary">
            デモ残高
          </div>
          <div className="text-lg font-semibold text-pp-text-primary">
            {formatCurrency({
              value: balance,
              supportZero: true,
              withSign: false,
              currency: 'JPY',
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Skeleton loading component for portfolio summary card
const PortfolioSummaryCardSkeleton = ({
  className,
}: {
  className?: string;
}) => (
  <div
    className={cn(
      'relative mb-5 rounded-2xl bg-white p-6 text-center shadow-sm',
      className,
    )}
  >
    {/* Demo account badge skeleton */}
    <div className="absolute right-4 top-4 h-6 w-16 animate-[shimmer_1.5s_infinite] rounded-xl bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>

    {/* Title skeleton */}
    <div className="mx-auto mb-1 h-3 w-32 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>

    {/* Total value skeleton */}
    <div className="mx-auto mb-4 h-8 w-48 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>

    {/* Change value skeleton */}
    <div className="mx-auto h-6 w-40 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>

    {/* Subtitle skeleton */}
    <div className="mx-auto mt-1 h-3 w-28 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
  </div>
);

export default PortfolioSummaryCard;
